import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import { previewSplitBilling } from "../../../../../services/table.services";
import { buildSplitItemPayload } from "./splitItemIdentity.ts";
import type {
  NormalizedSplitItem,
  PreviewResponse,
  SplitAmountMode,
  SplitPreviewDetail,
  SplitPreviewPayload,
  SplitType,
  TableDetail,
  UseSplitDiscountsProps,
  UseSplitDiscountsReturn,
} from "./types.ts";

export const SPLIT_PREVIEW_DEBOUNCE_MS = 400;

export const buildSplitPreviewPayload = ({
  tableId,
  tableDetail,
  lockedSplitMode,
  amountSplitPreview,
  splitAmountMode,
  splitPeopleCount,
  normalizedSplitItems,
}: {
  tableId?: string;
  tableDetail: TableDetail;
  lockedSplitMode: SplitType;
  amountSplitPreview: number;
  splitAmountMode: SplitAmountMode;
  splitPeopleCount: string;
  normalizedSplitItems: NormalizedSplitItem[];
}): SplitPreviewPayload | null => {
  const orderId = tableDetail?.activeOrder?._id;
  if (!tableId || !orderId) return null;

  if (lockedSplitMode === "amount") {
    if (!amountSplitPreview || amountSplitPreview <= 0) return null;
    const normalizedPeopleCount = Math.max(1, Number(splitPeopleCount) || 1);
    if (splitAmountMode === "equal" && normalizedPeopleCount < 2) return null;

    return {
      table_id: tableId,
      order_id: orderId,
      split_type: "amount",
      split_amount_mode: splitAmountMode,
      split_amount: amountSplitPreview,
      split_people_count: splitAmountMode === "equal" ? normalizedPeopleCount : undefined,
    };
  }

  if (!normalizedSplitItems.length) return null;

  return {
    table_id: tableId,
    order_id: orderId,
    split_type: "items",
    // Keep preview payload identity in sync with the final settlement payload.
    split_items: normalizedSplitItems.map((item) => buildSplitItemPayload(item)),
  };
};

const normalizeSplitPreviewDetail = (
  previewData: PreviewResponse["data"] | null | undefined,
): SplitPreviewDetail | null => {
  if (!previewData) return null;

  return {
    ...(previewData.price_detail || {}),
    remaining_after: previewData.remaining_after,
    settled_amount: previewData.settled_amount,
  };
};

export const useSplitDiscounts = ({
  tableId,
  tableDetail,
  lockedSplitMode,
  amountSplitPreview,
  splitAmountMode,
  splitPeopleCount,
  normalizedSplitItems,
  SWAL_SETTINGS,
  baseCurrentSplitTotal,
  hasSplitPayment,
  activeOrderKey,
}: UseSplitDiscountsProps): UseSplitDiscountsReturn => {
  const { t } = useTranslation();
  const [splitCouponCode, setSplitCouponCode] = useState("");
  const [splitGiftCardAmount, setSplitGiftCardAmount] = useState("");
  const [isCouponInputEnabled, setIsCouponInputEnabled] = useState(false);
  const [isGiftCardInputEnabled, setIsGiftCardInputEnabled] = useState(false);
  const [splitPreviewDetail, setSplitPreviewDetail] = useState<SplitPreviewDetail | null>(null);
  const [splitDiscountWarning, setSplitDiscountWarning] = useState("");
  const swalSettingsRef = useRef(SWAL_SETTINGS);

  useEffect(() => {
    swalSettingsRef.current = SWAL_SETTINGS;
  }, [SWAL_SETTINGS]);

  const previewPayload = useMemo(
    () =>
      buildSplitPreviewPayload({
        tableId,
        tableDetail,
        lockedSplitMode,
        amountSplitPreview,
        splitAmountMode,
        splitPeopleCount,
        normalizedSplitItems,
      }),
    [
      amountSplitPreview,
      lockedSplitMode,
      normalizedSplitItems,
      splitAmountMode,
      splitPeopleCount,
      tableDetail,
      tableId,
    ],
  );

  const normalizedSplitCouponCode = splitCouponCode.trim();
  const normalizedSplitGiftCardAmount = Math.max(0, Number(splitGiftCardAmount) || 0);
  const hasValidSplitGiftCardAmount = /^\d+$/.test(String(splitGiftCardAmount).trim());
  const isSplitCouponApplied = Boolean(
    isCouponInputEnabled &&
      normalizedSplitCouponCode &&
      splitPreviewDetail?.coupon_code_applied &&
      String(splitPreviewDetail?.coupon_code || "").trim() === normalizedSplitCouponCode,
  );
  const isSplitGiftCardApplied = Boolean(
    isGiftCardInputEnabled &&
      hasValidSplitGiftCardAmount &&
      normalizedSplitGiftCardAmount > 0 &&
      Number(splitPreviewDetail?.gift_card_amount || 0) === normalizedSplitGiftCardAmount,
  );
  const discountsLocked =
    lockedSplitMode === "amount" ? !amountSplitPreview : normalizedSplitItems.length === 0;
  const splitDiscountLockedReason =
    lockedSplitMode === "amount" ? t("label_enter_split_amount_first") : t("label_select_items_first");
  const maxSplitGiftCardAmount = Math.max(
    0,
    Math.round(baseCurrentSplitTotal - Number(splitPreviewDetail?.coupon_code_amount || 0)),
  );

  useEffect(() => {
    setSplitCouponCode("");
    setSplitGiftCardAmount("");
    setIsCouponInputEnabled(false);
    setIsGiftCardInputEnabled(false);
    setSplitPreviewDetail(null);
    setSplitDiscountWarning("");
  }, [activeOrderKey, hasSplitPayment]);

  const showPreviewError = useCallback(
    (message: string): void => {
      Swal.fire({ icon: "error", text: message, ...swalSettingsRef.current });
    },
    [],
  );

  const clearSplitDiscountWarning = useCallback((): void => {
    setSplitDiscountWarning("");
  }, []);

  const previewCurrentSelection = useCallback(
    async (options?: {
      couponCode?: string;
      giftCardAmount?: number;
      silent?: boolean;
    }): Promise<PreviewResponse["data"] | null> => {
      if (!previewPayload) return null;

      const { couponCode, giftCardAmount, silent = false } = options || {};

      try {
        const axiosResponse = await previewSplitBilling({
          ...previewPayload,
          coupon_code: couponCode || undefined,
          gift_card_amount: giftCardAmount || undefined,
        });
        const response = axiosResponse as unknown as PreviewResponse;

        if (!response?.success) {
          if (!silent) {
            showPreviewError(response?.message || "Unable to preview split payment");
          }
          return null;
        }

        return response?.data || null;
      } catch (error: unknown) {
        if (!silent) {
          const message =
            (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
            "Unable to preview split payment";
          showPreviewError(message);
        }
        return null;
      }
    },
    [previewPayload, showPreviewError],
  );

  useEffect(() => {
    if (!previewPayload) {
      setSplitPreviewDetail(null);
      setSplitDiscountWarning("");
      return;
    }

    let isCancelled = false;

    const refreshPreview = async (): Promise<void> => {
      const previewData = await previewCurrentSelection({
        couponCode: isSplitCouponApplied ? normalizedSplitCouponCode : undefined,
        giftCardAmount: isSplitGiftCardApplied ? normalizedSplitGiftCardAmount : undefined,
        silent: true,
      });

      if (isCancelled || !previewData) return;

      setSplitDiscountWarning("");
      setSplitPreviewDetail(normalizeSplitPreviewDetail(previewData));
    };

    // Debounced so rapid manual split-amount keystrokes don't fire a backend call per digit.
    const timeoutId = setTimeout(() => void refreshPreview(), SPLIT_PREVIEW_DEBOUNCE_MS);

    return () => {
      isCancelled = true;
      clearTimeout(timeoutId);
    };
  }, [
    isSplitCouponApplied,
    isSplitGiftCardApplied,
    normalizedSplitCouponCode,
    normalizedSplitGiftCardAmount,
    previewCurrentSelection,
    previewPayload,
  ]);

  const handleSplitCouponToggle = useCallback((checked: boolean): void => {
    setSplitDiscountWarning("");
    setIsCouponInputEnabled(checked);
    if (!checked) {
      setSplitCouponCode("");
      if (!isSplitGiftCardApplied) {
        setSplitPreviewDetail(null);
      }
    }
  }, [isSplitGiftCardApplied]);

  const handleSplitCouponCodeChange = useCallback((value: string): void => {
    setSplitDiscountWarning("");
    setSplitCouponCode(value);
  }, []);

  const handleApplySplitCoupon = useCallback(async (): Promise<void> => {
    setSplitDiscountWarning("");
    const couponCode = splitCouponCode.trim();
    if (!couponCode) {
      Swal.fire({ icon: "error", text: t("msg_enter_split_coupon_code"), ...SWAL_SETTINGS });
      return;
    }

    const previewData = await previewCurrentSelection({
      couponCode,
      giftCardAmount: isSplitGiftCardApplied ? normalizedSplitGiftCardAmount : undefined,
    });

    if (!previewData?.price_detail?.coupon_code_applied) return;

    setSplitPreviewDetail(normalizeSplitPreviewDetail(previewData));
    setIsCouponInputEnabled(true);
    Swal.fire({ icon: "success", text: t("coupon_code_applied"), ...SWAL_SETTINGS });
  }, [splitCouponCode, t, SWAL_SETTINGS, previewCurrentSelection, isSplitGiftCardApplied, normalizedSplitGiftCardAmount]);

  const handleRemoveSplitCoupon = useCallback((): void => {
    setSplitCouponCode("");
    setIsCouponInputEnabled(false);
    setSplitDiscountWarning("");
    if (!isSplitGiftCardApplied) {
      setSplitPreviewDetail(null);
    }
  }, [isSplitGiftCardApplied]);

  const handleSplitGiftCardToggle = useCallback((checked: boolean): void => {
    setSplitDiscountWarning("");
    setIsGiftCardInputEnabled(checked);
    if (!checked) {
      setSplitGiftCardAmount("");
      if (!isSplitCouponApplied) {
        setSplitPreviewDetail(null);
      }
    }
  }, [isSplitCouponApplied]);

  const handleSplitGiftCardAmountChange = useCallback((value: string): void => {
    setSplitDiscountWarning("");
    setSplitGiftCardAmount(value);
  }, []);

  const handleApplySplitGiftCard = useCallback(async (): Promise<void> => {
    setSplitDiscountWarning("");
    if (!hasValidSplitGiftCardAmount) {
      Swal.fire({ icon: "error", text: t("label_amount_integer_error"), ...SWAL_SETTINGS });
      return;
    }

    if (normalizedSplitGiftCardAmount <= 0) {
      Swal.fire({ icon: "error", text: t("msg_enter_valid_split_gift_card_amount"), ...SWAL_SETTINGS });
      return;
    }

    if (normalizedSplitGiftCardAmount > maxSplitGiftCardAmount) {
      Swal.fire({
        icon: "warning",
        text: `Gift card amount cannot exceed ¥${maxSplitGiftCardAmount} for this split`,
        ...SWAL_SETTINGS,
      });
      return;
    }

    const previewData = await previewCurrentSelection({
      couponCode: isSplitCouponApplied ? normalizedSplitCouponCode : undefined,
      giftCardAmount: normalizedSplitGiftCardAmount,
    });

    if (!previewData?.price_detail?.gift_card_amount) return;

    setSplitPreviewDetail(normalizeSplitPreviewDetail(previewData));
    setIsGiftCardInputEnabled(true);
    Swal.fire({ icon: "success", text: t("gift_card_applied"), ...SWAL_SETTINGS });
  }, [
    hasValidSplitGiftCardAmount,
    t,
    SWAL_SETTINGS,
    normalizedSplitGiftCardAmount,
    maxSplitGiftCardAmount,
    previewCurrentSelection,
    isSplitCouponApplied,
    normalizedSplitCouponCode,
  ]);

  const handleRemoveSplitGiftCard = useCallback((): void => {
    setSplitGiftCardAmount("");
    setIsGiftCardInputEnabled(false);
    setSplitDiscountWarning("");
    if (!isSplitCouponApplied) {
      setSplitPreviewDetail(null);
    }
  }, [isSplitCouponApplied]);

  return {
    splitCouponCode,
    splitGiftCardAmount,
    splitPreviewDetail,
    splitDiscountWarning,
    discountsLocked,
    splitDiscountLockedReason,
    handleSplitCouponToggle,
    handleSplitCouponCodeChange,
    handleApplySplitCoupon,
    handleRemoveSplitCoupon,
    handleSplitGiftCardToggle,
    handleSplitGiftCardAmountChange,
    handleApplySplitGiftCard,
    handleRemoveSplitGiftCard,
    isSplitCouponApplied,
    isSplitGiftCardApplied,
    normalizedSplitCouponCode,
    normalizedSplitGiftCardAmount,
    hasValidSplitGiftCardAmount,
    isCouponInputEnabled,
    isGiftCardInputEnabled,
    clearSplitDiscountWarning,
    previewCurrentSelection,
  };
};
